import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiSmartphone, FiMonitor, FiDownload, FiStar, FiZap, FiPlus, FiAlertCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import './Updates.css';

const Updates = () => {
  const [activeTab, setActiveTab] = useState('mobile');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const updatesData = {
    mobile: [
      {
        version: '3.0.0',
        date: '20 de Abril de 2026',
        title: 'Otimização e interfaces',
        isLatest: true,
        downloadUrl: '/downloads/android/csgtv.apk',
        changes: [
          { type: 'improvement', text: 'Botões do player redesenhados com identidade visual aprimorada.' },
          { type: 'new', text: 'Rotação automática ao entrar em tela cheia.' },
          { type: 'new', text: 'Novo botão para redimensionamento de janela.' },
          { type: 'new', text: 'Sistema de downloads para filmes e episódios.' },
          { type: 'improvement', text: 'Seção de downloads na aba “Continuar assistindo”.' },
          { type: 'improvement', text: 'Novo layout dos episódios em séries.' },
          { type: 'improvement', text: 'Navbar totalmente redesenhada.' },
          { type: 'improvement', text: 'Busca reconstruída com desempenho até 5x mais rápido.' }
        ]
      },
      {
        version: '2.0.1',
        date: '22 de janeiro de 2026',
        title: 'Controle parental e interfaces',
        isLatest: false,
        changes: [
          { type: 'improvement', text: 'Pagina inicial reformulada.' },
          { type: 'new', text: 'Controle parental adicionado para bloqueio de conteúdo.' }
        ]
      },
      {
        version: '2.0.0',
        date: '19 de Janeiro de 2026',
        title: 'Melhorias na Interface e Correções',
        isLatest: false,
        changes: [
          { type: 'improvement', text: 'Adicionado suporte a qualidade de até 4k Ultra HD com HDR.' },
          { type: 'fix', text: 'Resolvido problema de login.' },
          { type: 'fix', text: 'Ajuste no tamanho dos botões em telas menores.' }
        ]
      }
    ],
    desktop: [
      {
        version: '3.0.0',
        date: '20 de Abril de 2026',
        title: 'Otimização e correções',
        isLatest: true,
        downloadUrl: '/downloads/windows/CSGTV.exe',
        changes: [
          { type: 'improvement', text: 'Persistência da posição de scroll ao retornar da página de detalhes de filmes.' },
          { type: 'improvement', text: 'Comportamento de tela cheia ajustado para sobrepor corretamente todas as janelas.' },
          { type: 'improvement', text: 'Carrossel de filmes e séries aprimorado para navegação mais fluida.' },
          { type: 'improvement', text: 'Persistência de estado e progresso em séries e filmes otimizada.' },
          { type: 'fix', text: 'Corrigido desaparecimento dos controles do player em alguns dispositivos.' }
        ]
      },
      {
        version: '2.0.1',
        date: '22 de janeiro de 2026',
        title: 'Controle parental e correções',
        isLatest: false,
        changes: [
          { type: 'new', text: 'Controle parental adicionado para bloqueio de conteúdo.' },
          { type: 'fix', text: 'Implementado carregamento assíncrono de conteúdo para otimizar performance.' }
        ]
      },
      {
        version: '1.4.2',
        date: '05 de Dezembro de 2025',
        title: 'Atalhos e Performance',
        isLatest: false,
        changes: [
          { type: 'new', text: 'Novos atalhos de teclado para controle de reprodução.' },
          { type: 'improvement', text: 'Melhoria na interface para maior usabilidade.' },
          { type: 'fix', text: 'Correção de erro ao minimizar para a bandeja do sistema.' }
        ]
      }
    ]
  };

  const getChangeTypeLabel = (type) => {
    switch(type) {
      case 'new': return 'Novo Recurso';
      case 'improvement': return 'Melhoria';
      case 'fix': return 'Correção';
      default: return 'Geral';
    }
  };

  const getChangeTypeIcon = (type) => {
    switch(type) {
      case 'new': return <FiPlus />;
      case 'improvement': return <FiZap />;
      case 'fix': return <FiAlertCircle />;
      default: return <FiStar />;
    }
  };

  return (
    <section className="updates-section">
      <div className="updates-container">
        <div className="updates-header">
          <Link to="/" className="back-link">
            <FiArrowLeft /> Voltar para o início
          </Link>
          
          <motion.div 
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h1>Versões & Atualizações</h1>
            <p className="updates-subtitle">
              Acompanhe a jornada de evolução do CSG TV. Cada versão traz inovações focadas em sua melhor experiência.
            </p>
          </motion.div>

          <motion.div 
            className="platform-toggle-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="toggle-wrapper">
              <div className="toggle-label">
                Plataforma
              </div>
              <div className="platform-toggle">
                <button 
                  className={`toggle-btn ${activeTab === 'mobile' ? 'active' : ''}`}
                  onClick={() => setActiveTab('mobile')}
                >
                  <FiSmartphone /> Mobile
                </button>
                <button 
                  className={`toggle-btn ${activeTab === 'desktop' ? 'active' : ''}`}
                  onClick={() => setActiveTab('desktop')}
                >
                  <FiMonitor /> Desktop
                </button>
                <div className={`toggle-slider ${activeTab}`} />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="timeline">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              className="updates-list"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
            >
              {updatesData[activeTab].map((update, index) => (
                <motion.div 
                  key={update.version}
                  className={`update-card ${update.isLatest ? 'latest' : ''}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7, delay: index * 0.1 }}
                >
                  <div className="update-meta">
                    <div className="version-badge">
                      <span className="version-number">v{update.version}</span>
                      {update.isLatest && <span className="latest-tag">Última Versão</span>}
                      <span className="update-date">{update.date}</span>
                    </div>
                    
                    {update.isLatest && (
                      <motion.a 
                        href={update.downloadUrl}
                        className="update-download-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FiDownload className="download-icon" />
                        <div className="download-info">
                          <span className="download-text">Baixar v{update.version}</span>
                        </div>
                      </motion.a>
                    )}
                  </div>

                  <div className="update-content">
                    <h2 className="update-title">{update.title}</h2>
                    
                    <div className="changes-list-container">
                      <div className="changes-list-header">
                        <span className="changes-title">Log de Alterações</span>
                        <span className="changes-count">{update.changes.length} itens</span>
                      </div>
                      
                      <div className="changes-list">
                        {update.changes.map((change, idx) => (
                          <div key={idx} className={`change-item ${change.type}`}>
                            <div className="change-type-icon">
                              {getChangeTypeIcon(change.type)}
                            </div>
                            <div className="change-content-main">
                              <span className="change-type-label">
                                {getChangeTypeLabel(change.type)}
                              </span>
                              <p className="change-text">{change.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Updates;
